import React, { useEffect, useMemo, useState } from 'react';
import {
  Flex, Grid, Section, Input, Select, RadioGroup, PasswordInput
} from 'onefaceui-react-components';
import PropTypes from 'prop-types';

import BaseFormV2 from 'react/containers/layout/Crud/BaseFormV2';

import defaultFetch from 'react/api/sagas/fetch/defaultFetch';
import queryClient from 'react/services/queryClient';
import { getResourceTo } from 'react/utils/utils';
import { queryKey, useDataSyncConfiguration } from 'react/queries/DataSyncConfiguration';
import { SourceType } from './utils';

function DataSyncConfigurationForm({ id }) {
  const [sourceType, setSourceType] = useState(null);
  const [destinationType, setDestinationType] = useState(null);

  const { data = [], isFetching } = useDataSyncConfiguration();

  const isEdit = useMemo(() => !!id, [id]);

  const sourceAndDestinationOptions = [
    {
      label: getResourceTo('dataSyncConfiguration.sourceTypes.S3'),
      value: SourceType.S3
    },
    {
      label: getResourceTo('dataSyncConfiguration.sourceTypes.SFTP'),
      value: SourceType.SFTP
    }
  ];

  const getSFTPFormValues = (values, dataFrom) => {
    const settings = {
      url: values[`${dataFrom}-url`],
      username: values[`${dataFrom}-username`],
      destinationFolder: values[`${dataFrom}-destinationFolder`],
    };
    const passwordValue = values[`${dataFrom}-password`];

    if (passwordValue) {
      settings.password =  passwordValue;
    }

    const payload = {
      [dataFrom]: {
        type: 'SFTP',
        settings
      }
    };

    return payload;
  };

  const getSFTPFieldsNamesFor = (prefix) => [`${prefix}-url`, `${prefix}-username`, `${prefix}-password`, `${prefix}-destinationFolder`];

  const parsePayload = (values) => {
    const payload = {
      name: values.name,
      status: !!values.status
    };

    if (sourceType === SourceType.SFTP) {
      const sftpValues = getSFTPFormValues(values, 'source');

      Object.assign(payload, sftpValues);
    } else {
      Object.assign(payload, {
        source: {
          type: 'S3',
          settings: {}
        }
      });
    }

    if (destinationType === SourceType.SFTP) {
      const sftpValues = getSFTPFormValues(values, 'destination');

      Object.assign(payload, sftpValues);
    } else {
      Object.assign(payload, {
        destination: {
          type: 'S3',
          settings: {}
        }
      });
    }

    if (isEdit) {
      payload.id = id;
    }

    return payload;
  };

  const handleSubmit = async (values) => {
    const payload = parsePayload(values);

    const method = isEdit ? 'PATCH' : 'POST';
    const url = isEdit ? `ArchiveProcessor/Configuration/${id}` : 'ArchiveProcessor/Configuration';

    const response = await defaultFetch(url, payload, method);

    return response;
  };

  const isLoadingForm = useMemo(() => {
    if (!isEdit) return false;

    if (isEdit) return isFetching;

    return false;
  }, [isEdit, isFetching]);

  const requiredFields = useMemo(() => {
    const fields = ['name', 'status'];

    if (sourceType === SourceType.SFTP) {
      const sftpFields = getSFTPFieldsNamesFor('source');

      fields.concat(sftpFields);
    }

    if (destinationType === SourceType.SFTP) {
      const sftpFields = getSFTPFieldsNamesFor('destination');

      fields.concat(sftpFields);
    }

    return fields;
  }, [sourceType, destinationType, isEdit]);

  const initialData = useMemo(() => {
    const findRegister = data.find((item) => item.id === id);

    if (!findRegister) return {};

    const {
      name,
      status,
      source,
      destination
    } = findRegister;

    const formData = {
      name,
      status: status ? 1 : 0,
      sourceType: source?.type,
      destinationType: destination?.type
    };

    if (source) {
      const {
        url,
        username,
        password,
        destinationFolder
      } = source?.settings ?? {};

      Object.assign(formData, {
        'source-url': url,
        'source-username': username,
        'source-password': password,
        'source-destinationFolder': destinationFolder
      });
    }

    if (destination) {
      const {
        url,
        username,
        password,
        destinationFolder
      } = destination?.settings ?? {};

      Object.assign(formData, {
        'destination-url': url,
        'destination-username': username,
        'destination-password': password,
        'destination-destinationFolder': destinationFolder
      });
    }

    return formData;
  }, [data, id]);

  useEffect(() => {
    if (Object.keys(initialData).length && !isFetching) {
      setSourceType(initialData.sourceType);
      setDestinationType(initialData.destinationType);
    }
  }, [initialData, isFetching]);

  return (
    <BaseFormV2
      id={id}
      entity="DataSyncConfiguration"
      dataTestId="DataSyncConfigurationForm"
      defaultValues={initialData}
      isLoading={isLoadingForm}
      saveAction={handleSubmit}
      requiredFields={requiredFields}
      afterSubmit={() => {
        queryClient.invalidateQueries(queryKey);
      }}
    >
      <Flex
        flexDirection="column"
        gap={16}
      >
        <Section label="General information">
          <Grid
            templateColumns="0.4fr"
            breakpoints={{
              md: {
                templateColumns: '0.5fr'
              }
            }}
            gap={16}
          >
            <Flex gap={16}>
              <Input
                dataTestId="name"
                label={getResourceTo('dataSyncConfiguration.fields.description')}
                name="name"
                required
              />
              <RadioGroup
                dataTestId="status"
                label={getResourceTo('dataSyncConfiguration.fields.status')}
                name="status"
                options={[
                  {
                    label: getResourceTo('Integration.Switch.EnabledLabel'),
                    value: 1
                  },
                  {
                    label: getResourceTo('Integration.Switch.DisabledLabel'),
                    value: 0
                  }
                ]}
              />
            </Flex>

            <Flex gap={16}>
              <Select
                dataTestId="sourceType"
                label={getResourceTo('dataSyncConfiguration.fields.source')}
                name="sourceType"
                options={sourceAndDestinationOptions}
                onChange={(option) => setSourceType(option.value)}
                required
              />
              <Select
                dataTestId="destinationType"
                label={getResourceTo('dataSyncConfiguration.fields.destination')}
                name="destinationType"
                options={sourceAndDestinationOptions}
                onChange={(option) => setDestinationType(option.value)}
                required
              />
            </Flex>
          </Grid>
        </Section>

        {sourceType === SourceType.SFTP && (
          <Section label={getResourceTo('Source integration settings')}>
            <Grid
              templateColumns="0.4fr"
              breakpoints={{
                md: {
                  templateColumns: '0.5fr'
                }
              }}
              gap={16}
            >
              <Flex gap={16}>
                <Input
                  dataTestId="source-url"
                  label={getResourceTo('dataSyncConfiguration.fields.serverAddress')}
                  name="source-url"
                  required
                />
              </Flex>
              <Flex gap={16}>
                <Input
                  dataTestId="source-username"
                  label={getResourceTo('dataSyncConfiguration.fields.username')}
                  name="source-username"
                  required
                />
                <PasswordInput
                  dataTestId="source-password"
                  label={getResourceTo('dataSyncConfiguration.fields.password')}
                  name="source-password"
                  required={!isEdit}
                />
              </Flex>
              <Flex gap={16}>
                <Input
                  dataTestId="source-destinationFolder"
                  label={getResourceTo('dataSyncConfiguration.fields.serverFolder')}
                  name="source-destinationFolder"
                  required
                />
              </Flex>
            </Grid>
          </Section>
        )}
        {destinationType === SourceType.SFTP && (
          <Section label={getResourceTo('Destination integration settings')}>
            <Grid
              templateColumns="0.4fr"
              breakpoints={{
                md: {
                  templateColumns: '0.5fr'
                }
              }}
              gap={16}
            >
              <Flex gap={16}>
                <Input
                  dataTestId="destination-url"
                  label={getResourceTo('dataSyncConfiguration.fields.serverAddress')}
                  name="destination-url"
                  required
                />
              </Flex>
              <Flex gap={16}>
                <Input
                  dataTestId="destination-username"
                  label={getResourceTo('dataSyncConfiguration.fields.username')}
                  name="destination-username"
                  required
                />
                <PasswordInput
                  dataTestId="destination-password"
                  label={getResourceTo('dataSyncConfiguration.fields.password')}
                  name="destination-password"
                  required={!isEdit}
                />
              </Flex>
              <Flex gap={16}>
                <Input
                  dataTestId="destination-destinationFolder"
                  label={getResourceTo('dataSyncConfiguration.fields.serverFolder')}
                  name="destination-destinationFolder"
                  required
                />
              </Flex>
            </Grid>
          </Section>
        )}
      </Flex>
    </BaseFormV2>
  );
}

DataSyncConfigurationForm.propTypes = {
  id: PropTypes.string
};

DataSyncConfigurationForm.defaultProps = {
  id: undefined
};

export default DataSyncConfigurationForm;
